import { animate, AnimationTriggerMetadata, state, style, transition, trigger } from '@angular/animations';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectorRef, Component, ContentChildren, NgZone, OnDestroy, QueryList } from '@angular/core';
import { merge, of, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { MyLeadLayoutItem } from './my-lead-layout.directive';

const MOBILE_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1200;

const itemExpansionAnimation: AnimationTriggerMetadata = trigger('itemExpansion', [
  state('expanded', style({ width: 'calc(100% - 78px)' })),
  state('collapsed', style({ width: '78px' })),
  state('default', style({ width: '50%' })),
  state('default-mobile', style({ width: '100%' })),
  transition('* <=> *', animate('600ms ease-in-out'))
]);

const contentExpansionAnimation: AnimationTriggerMetadata = trigger('contentExpansion', [
  state('expanded', style({ height: '*', visibility: 'visible' })),
  state('default-mobile', style({ height: '*', visibility: 'visible' })),
  state('collapsed', style({ height: '0px', visibility: 'hidden' })),
  transition('* <=> *', animate('600ms ease-in-out'))
]);

@Component({
  selector: 'mylead-layout',
  templateUrl: './my-lead-layout.component.html',
  styleUrls: ['./my-lead-layout.component.scss'],
  animations: [itemExpansionAnimation, contentExpansionAnimation]
})
export class MyLeadLayoutComponent implements OnDestroy, AfterViewInit {
  @ContentChildren(MyLeadLayoutItem) items: QueryList<MyLeadLayoutItem>;

  public layoutItems: MyLeadLayoutItem[] = [];

  private windowWidth: number = 0;

  private readonly viewportChange = this.viewportRuler
    .change(0)
    .pipe(filter(({ isTrusted }) => isTrusted))
    .subscribe(() => this.ngZone.run(() => this.setExpandedSection()));

  public get isMobile(): boolean {
    return this.windowWidth < MOBILE_BREAKPOINT;
  }

  public get isTablet(): boolean {
    return this.windowWidth > MOBILE_BREAKPOINT && this.windowWidth <= DESKTOP_BREAKPOINT;
  }

  public get isDesktop(): boolean {
    return this.windowWidth > DESKTOP_BREAKPOINT;
  }

  private destroy$ = new Subject<void>();

  constructor(private readonly viewportRuler: ViewportRuler, private readonly ngZone: NgZone, private readonly cdr: ChangeDetectorRef) {}

  ngAfterViewInit() {
    merge(this.items.changes, of(this.items))
      .pipe(
        map(() => this.items.toArray()),
        takeUntil(this.destroy$)
      )
      .subscribe(items => {
        this.layoutItems = items;
        this.setExpandedSection();
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy() {
    this.viewportChange.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toggleState = (index: number) => {
    this.isMobile ? this.toggMobile(index) : this.toggleDesktop(index);
  };

  private toggMobile = (index: number) => {
    this.layoutItems[index].setState(this.layoutItems[index].isCollapsed() ? 'expanded' : 'collapsed');
  };

  private toggleDesktop = (index: number) => {
    this.layoutItems.forEach((item, i) => item.toggle(index === i, this.isTablet));
  };

  private setExpandedSection(): void {
    const { width } = this.viewportRuler.getViewportSize();

    this.windowWidth = width;

    if (this.isDesktop) this.layoutItems.forEach((item, i) => item.setState('default'));

    if (this.isTablet) this.layoutItems.forEach((item, i) => item.setState(i ? 'expanded' : 'collapsed'));

    if (this.isMobile) this.layoutItems.forEach((item, i) => item.setState('default-mobile'));
  }
}
